import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { apiHomeBrandLogos } from '../../lib/api';
import { brandLogoSrc } from '../../lib/logodev';
import type { HomeBrandLogo } from '../../types';

const FALLBACK_BRANDS: HomeBrandLogo[] = [
  { id: -1, name: 'Apple', logo_type: 'logodev_name', logo_value: 'Apple' },
  { id: -2, name: 'Samsung', logo_type: 'logodev_name', logo_value: 'Samsung' },
  { id: -3, name: 'Xiaomi', logo_type: 'logodev_name', logo_value: 'Xiaomi' },
  { id: -4, name: 'Motorola', logo_type: 'logodev_name', logo_value: 'Motorola' },
  { id: -5, name: 'OPPO', logo_type: 'logodev_name', logo_value: 'OPPO' },
  { id: -6, name: 'ZTE', logo_type: 'logodev_name', logo_value: 'ZTE' },
  { id: -7, name: 'Infinix', logo_type: 'custom', logo_value: 'https://assets.tecnicelu.store/logos/brands/infinix.png' },
];

function BrandLogo({ brand, suffix = '' }: { brand: HomeBrandLogo; suffix?: string }) {
  return (
    <div key={`${brand.id}-${suffix}`} title={brand.name} className="flex shrink-0 items-center justify-center overflow-hidden" style={{ width: 'clamp(36px, 11.5vw, 72px)', height: 'clamp(28px, 8.5vw, 52px)' }}>
      <img src={brandLogoSrc(brand.logo_type, brand.logo_value, brand.name)} alt={`${brand.name} logo`} loading="lazy" decoding="async" className="h-full w-full max-h-full max-w-full object-contain" />
    </div>
  );
}

export default function BrandStrip({ logos }: { logos?: HomeBrandLogo[] }) {
  const { t } = useI18n();
  const [brands, setBrands] = useState<HomeBrandLogo[]>(FALLBACK_BRANDS);
  const sectionRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (logos) return;
    let cancelled = false;
    let observer: IntersectionObserver | null = null;
    const start = () => {
      apiHomeBrandLogos().then((items) => {
        if (!cancelled && items.length) setBrands(items);
      }).catch(() => {
        if (!cancelled) setBrands(FALLBACK_BRANDS);
      });
    };
    if ('IntersectionObserver' in window && sectionRef.current) {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          start();
          observer?.disconnect();
        }
      }, { rootMargin: '400px 0px' });
      observer.observe(sectionRef.current);
    } else {
      const timer = window.setTimeout(start, 1500);
      return () => { cancelled = true; window.clearTimeout(timer); };
    }
    return () => { cancelled = true; observer?.disconnect(); };
  }, [logos]);

  const displayBrands = logos ?? brands;
  const carousel = displayBrands.length > FALLBACK_BRANDS.length;
  const logoItems = carousel ? [...displayBrands, ...displayBrands] : displayBrands;

  return (
    <section ref={sectionRef} id="brand-strip" className="border-y border-line/60 bg-surface/45 py-6 sm:py-8 md:py-10" aria-label={t('brands_we_carry')}>
      <div className="container mx-auto px-3 sm:px-4">
        <div className="mb-4 flex items-center justify-center gap-2 sm:gap-3">
          <span aria-hidden="true" className="h-px w-6 bg-brand/45 sm:w-10 md:w-14" />
          <h2 className="rounded-full border border-brand/25 bg-brand/10 px-3 py-1.5 font-display text-xs font-bold uppercase tracking-[0.12em] text-ink shadow-sm shadow-brand/10 sm:px-4 sm:py-2 sm:text-sm">
            {t('brands_we_carry')}
          </h2>
          <span aria-hidden="true" className="h-px w-6 bg-accent/45 sm:w-10 md:w-14" />
        </div>
        <div className="flex justify-center overflow-hidden">
          <div className={`flex w-fit flex-nowrap items-center gap-0.5 sm:gap-2 md:gap-3 rounded-xl border border-glass-line/25 bg-gradient-to-br from-white/75 via-white/55 to-brand/25 py-2 shadow-sm shadow-canvas/30 backdrop-blur-md ${carousel ? 'brand-strip-marquee max-w-none' : 'max-w-3xl justify-center'}`}>
            {logoItems.map((brand, index) => <BrandLogo key={`${brand.id}-${index}`} brand={brand} suffix={String(index)} />)}
          </div>
        </div>
      </div>
    </section>
  );
}
